import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import notificationPreferencesService from '../../../services/notificationPreferencesService';
import { toast } from '../../../components/ui/Toast';

const SMSControls = ({ preferences, onUpdate }) => {
  const [testing, setTesting] = useState(false);

  const frequencyOptions = [
    { value: 'instant', label: 'Instant', description: 'Receive SMS immediately for each notification' },
    { value: 'daily', label: 'Daily Digest', description: 'One SMS per day with summary' },
    { value: 'weekly', label: 'Weekly Summary', description: 'One SMS per week with highlights' },
    { value: 'off', label: 'Off', description: 'No SMS notifications' },
  ];

  const handleTestSMS = async () => {
    if (!preferences?.phoneNumber) {
      toast?.warning('Please enter a phone number first');
      return;
    }

    const validation = notificationPreferencesService?.validatePhoneNumber(preferences?.phoneNumber);
    if (!validation?.valid) {
      toast?.error('Invalid phone number format', {
        description: 'Please enter a valid 10-digit US phone number',
      });
      return;
    }

    setTesting(true);
    try {
      const { error } = await notificationPreferencesService?.testSMSNotification(validation?.formatted);
      if (error) throw error;

      toast?.success('Test SMS sent successfully', {
        description: 'Check your phone for the test message',
      });
    } catch (error) {
      console.error('Error sending test SMS:', error);
      toast?.error('Failed to send test SMS');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="MessageSquare" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">SMS Notification Controls</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure SMS frequency and delivery preferences. Standard messaging rates may apply.
          </p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Phone Number Input */}
        <div>
          <Input
            label="Phone Number"
            type="tel"
            placeholder="(555) 123-4567"
            value={preferences?.phoneNumber || ''}
            onChange={(e) => onUpdate({ phoneNumber: e?.target?.value })}
            description="Enter your 10-digit US phone number"
          />
        </div>

        {/* Carrier Info */}
        <div>
          <Input
            label="Carrier (Optional)"
            type="text"
            placeholder="e.g., Verizon, AT&T, T-Mobile"
            value={preferences?.carrierInfo || ''}
            onChange={(e) => onUpdate({ carrierInfo: e?.target?.value })}
            description="Your mobile carrier for delivery optimization"
          />
        </div>

        {/* Frequency Options */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">SMS Frequency</label>
          {frequencyOptions?.map((option) => (
            <Checkbox
              key={option?.value}
              checked={preferences?.frequency === option?.value}
              onChange={() => onUpdate({ frequency: option?.value })}
              label={option?.label}
              description={option?.description}
            />
          ))}
        </div>

        {/* Test SMS Button */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestSMS}
            loading={testing}
            disabled={testing || !preferences?.phoneNumber || preferences?.frequency === 'off'}
            iconName="Send"
          >
            Send Test SMS
          </Button>
          <p className="text-xs text-muted-foreground">
            Test your SMS configuration before saving
          </p>
        </div>

        {/* Compliance Notice */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">SMS Compliance Notice</p>
            <p>
              By enabling SMS notifications, you consent to receive automated text messages from DraftIQ.
              Message and data rates may apply. Reply STOP to opt out at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSControls;